import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Alert, Button } from 'antd';

import { useUserStore, toStoreUser } from '@/store/userStore';
import { UserService } from '@/services/userService';
import { identifyUser, resetAnalytics } from '@/services/analytics';
import { APIResponseError } from '@/services/backend';
import { ROUTES } from '@/routes';
import { saveRedirectPath } from '@/utils';
import { getStoredGuestSessionToken, clearStoredGuestSessionToken } from '@/utils/guestSession';
import { useSessionToken } from '@/hooks';
import { FullScreenSpinner } from '@/components/FullScreenSpinner';
import styles from './ProtectedRoute.module.scss';

interface ProtectedRouteProps {
  children?: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const { isLoaded, isSignedIn, getToken, signOut } = useAuth();
  const { getToken: getGuestOrClerkToken } = useSessionToken();
  const { user, isLoading, setUser, setToken, setLoading, clearUser } = useUserStore();
  const [guestLoadFailed, setGuestLoadFailed] = useState(false);
  const [guestRetry, setGuestRetry] = useState(0);
  // Set only when a stored guest token was just rejected (401/403) — a
  // brand-new visitor with no token falls through to the default sign-in
  // redirect below instead, since the app can't tell the two apart.
  const [guestSessionExpired, setGuestSessionExpired] = useState(false);
  // A code-redemption guest never signs in via Clerk, so a stored token is what
  // stands in for their session. Read only once Clerk has loaded and confirmed
  // nobody is signed in, so a real Clerk session always wins (see useSessionToken).
  const guestToken = isLoaded && !isSignedIn ? getStoredGuestSessionToken() : null;

  useEffect(() => {
    if (guestToken) {
      const fetchGuestUser = async () => {
        try {
          setLoading(true);
          setGuestLoadFailed(false);
          const token = await getGuestOrClerkToken();
          if (!token) {
            clearUser();
            resetAnalytics();
            return;
          }
          setToken(token);
          const backendUser = await new UserService(token).getMe();
          setUser(toStoreUser(backendUser));
          identifyUser(backendUser, { isGuest: true });
        } catch (error) {
          console.error('Failed to fetch guest user data:', error);
          // Discard the token only when the API actually rejects it. It's a flat
          // 24h grant with no refresh path and the code behind it is already
          // spent, so dropping it on a transient 500 or a dropped connection
          // would lock the candidate out for good — those get a retry instead.
          if (error instanceof APIResponseError && (error.status === 401 || error.status === 403)) {
            clearStoredGuestSessionToken();
            clearUser();
            resetAnalytics();
            setGuestSessionExpired(true);
          } else {
            setGuestLoadFailed(true);
          }
        } finally {
          setLoading(false);
        }
      };

      void fetchGuestUser();
      return;
    }

    if (!isLoaded || !isSignedIn) return;

    const fetchBackendUser = async () => {
      try {
        setLoading(true);
        const token = await getToken();

        if (token) {
          setToken(token);
          const backendUser = await new UserService(token).getMe();
          setUser(toStoreUser(backendUser));
          identifyUser(backendUser, { isGuest: false });
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        if (error instanceof APIResponseError && error.status === 401) {
          clearUser();
          resetAnalytics();
          useUserStore.persist.clearStorage();
          // Signing out makes any stored guest token authoritative again (see
          // `guestToken` above), so clear it too rather than landing them there.
          clearStoredGuestSessionToken();
          void signOut({ redirectUrl: ROUTES.LOGIN });
        }
      } finally {
        setLoading(false);
      }
    };

    void fetchBackendUser();
  }, [
    guestToken,
    guestRetry,
    isLoaded,
    isSignedIn,
    getToken,
    getGuestOrClerkToken,
    setUser,
    setToken,
    setLoading,
    clearUser,
    signOut,
  ]);

  useEffect(() => {
    if (!guestToken && isLoaded && !isSignedIn) {
      clearUser();
      resetAnalytics();
    }
  }, [guestToken, isLoaded, isSignedIn, clearUser]);

  if (guestToken) {
    if (guestLoadFailed) {
      return (
        <div className={styles.guestError}>
          <Alert
            className={styles.guestErrorAlert}
            type="error"
            showIcon
            message="Couldn't load your session"
            description="Your access code is still valid. Check your connection and try again."
          />
          <Button type="primary" onClick={() => setGuestRetry((attempt) => attempt + 1)}>
            Try again
          </Button>
        </div>
      );
    }
    if (isLoading || !user) {
      return <FullScreenSpinner message="Loading..." />;
    }
    return children ? <>{children}</> : <Outlet />;
  }

  if (!isLoaded) {
    return <FullScreenSpinner message="Loading..." />;
  }

  if (isSignedIn && isLoading) {
    return <FullScreenSpinner message="Loading user data..." />;
  }

  if (guestSessionExpired) {
    return <Navigate to={ROUTES.REDEEM} state={{ sessionExpired: true }} replace />;
  }

  if (!isSignedIn) {
    saveRedirectPath(location.pathname + location.search + location.hash);
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (!user) {
    return <FullScreenSpinner message="Loading user data..." />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
